import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ArrowDownward,
    ArrowUpward,
    ContentCopy,
    Delete,
    ExpandMore,
    Star,
    StarBorder,
} from "@mui/icons-material";

import { IChangeType } from "../../interfaces/IChangeType";
import { IChange } from "../../interfaces/IChange";
import { CustomeTextField } from "../core/CustomeTextField";
import { CustomEditor } from "../core/CustomEditor";

export interface IProps {
    currentChange: IChange;
    changeArray: IChange[];
    setChangeArray: Function;
    changeTypeArray: IChangeType[];
    /** Position in der Liste — für die Sortierpfeile. */
    index: number;
    onMove: (index: number, direction: -1 | 1) => void;
    onDuplicate: (change: IChange) => void;
}

export const ChangeAccordion: React.FC<IProps> = ({
    currentChange,
    changeArray,
    setChangeArray,
    changeTypeArray,
    index,
    onMove,
    onDuplicate,
}) => {
    const [editChange, setEditChange] = useState(currentChange);

    const onRemove = () => {
        if (
            !window.confirm(
                `Eintrag „${editChange.title || "ohne Titel"}" wirklich entfernen?`
            )
        )
            return;
        setChangeArray([...changeArray.filter(x => x.id !== editChange.id)]);
    };

    const toggleHighlight = () => {
        setEditChange({ ...editChange, isHighlighted: !editChange.isHighlighted });
    };

    useEffect(() => {
        setChangeArray([...changeArray.map(x => (x.id === editChange.id ? editChange : x))]);
    }, [editChange]);

    const typeName =
        changeTypeArray.find(x => x.id === editChange.ChangeType)?.changeType ?? "Ohne Art";
    const isIncomplete = (editChange.title || "").trim() === "";

    return (
        <Accordion defaultExpanded={isIncomplete}>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center", width: "100%", pr: 1 }}
                >
                    <Chip size="small" label={typeName} />
                    <Typography sx={{ flexGrow: 1 }}>
                        {editChange.title || (
                            <em style={{ opacity: 0.6 }}>Neuer Eintrag — bitte Titel vergeben</em>
                        )}
                    </Typography>
                    {editChange.isHighlighted && (
                        <Tooltip title="Highlight">
                            <Star fontSize="small" color="warning" />
                        </Tooltip>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
                            <Tooltip title="Nach oben">
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={index === 0}
                                        onClick={() => onMove(index, -1)}
                                    >
                                        <ArrowUpward fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Nach unten">
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={index === changeArray.length - 1}
                                        onClick={() => onMove(index, 1)}
                                    >
                                        <ArrowDownward fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={
                                    editChange.isHighlighted
                                        ? "Highlight entfernen"
                                        : "Als Highlight markieren"
                                }
                            >
                                <IconButton size="small" onClick={toggleHighlight}>
                                    {editChange.isHighlighted ? (
                                        <Star fontSize="small" color="warning" />
                                    ) : (
                                        <StarBorder fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Eintrag duplizieren">
                                <IconButton size="small" onClick={() => onDuplicate(editChange)}>
                                    <ContentCopy fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Eintrag entfernen">
                                <IconButton size="small" color="error" onClick={onRemove}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <CustomeTextField
                            attr="ChangeType"
                            label="Art"
                            object={editChange}
                            setObject={setEditChange}
                            type="select"
                        >
                            {changeTypeArray.map(changeType => (
                                <MenuItem
                                    key={`select-changetype-${changeType.id}`}
                                    value={changeType.id}
                                >
                                    {changeType.changeType}
                                </MenuItem>
                            ))}
                        </CustomeTextField>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <CustomeTextField
                            attr="title"
                            label="Titel"
                            object={editChange}
                            setObject={setEditChange}
                            type="string"
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomeTextField
                            attr="dinoAcademyLink"
                            label="DiNo Academy Link"
                            object={editChange}
                            setObject={setEditChange}
                            type="string"
                            null
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Beschreibung
                        </Typography>
                        <Box sx={{ border: "1px solid rgba(0,0,0,0.23)", borderRadius: 1, p: 1 }}>
                            <CustomEditor
                                txt={editChange.descriptionHtml || ""}
                                setTxt={(txt: string) =>
                                    setEditChange({ ...editChange, descriptionHtml: txt })
                                }
                            />
                        </Box>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};
